import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import { filterByLabel } from "@/lib/filterByLabel";
import { DataOperationsService, SchemaService } from "@/services/APIs";
import { ColumnMapping } from "@/utils/types/dataOperator";
import { useQuery } from "@tanstack/react-query";
import { Alert, Card, Form, Select, Space, Spin, Typography } from "antd";
import { Play, RefreshCw } from "lucide-react";
import { useState } from "react";

const { Text } = Typography;

interface ColumnMappingStepProps {
  selectedSchema: string;
  fileColumns: string[];
  importId: string | null;
  isUpdatingMapping: boolean;
  isRunningImport: boolean;
  isMappingSaved: boolean;
  onMappingUpdate: (saved: boolean) => void;
  onImportRun: () => void;
  onResetMapping: () => void;
  disabled?: boolean;
}

const ColumnMappingStep = ({
  selectedSchema,
  fileColumns,
  importId,
  isUpdatingMapping,
  isRunningImport,
  isMappingSaved,
  onMappingUpdate,
  onImportRun,
  disabled = false,
}: ColumnMappingStepProps) => {
  const [mappingError, setMappingError] = useState<string>("");
  const [form] = Form.useForm();

  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", selectedSchema],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          selectedSchema ?? ""
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!selectedSchema,
  });

  const handleUpdateMapping = async (
    importId: string,
    mapping: ColumnMapping[]
  ) => {
    const response = await DataOperationsService.updateColumnMapping(importId, {
      column_mapping: mapping,
    });
    if (response) {
      onMappingUpdate(true);
    }
  };

  // Get database column names for mapping (filter out system fields)
  const dbColumns =
    columnGridData?.grid
      ?.filter((field: any) => {
        const systemFields = [
          "id",
          "is_active",
          "created_on",
          "updated_on",
          "created_by",
          "updated_by",
          "tenant",
          "created_by_id",
          "updated_by_id",
          "tenant_id",
        ];
        return !systemFields.includes(field.field_name.toLowerCase());
      })
      ?.map((field: any) => ({
        label: field.field_title,
        value: field.field_name.toLowerCase(),
      })) ?? [];

  // Filter file columns to exclude system fields from mapping
  const getMappableFileColumns = () => {
    const systemFields = [
      "id",
      "is_active",
      "created_on",
      "updated_on",
      "created_by",
      "updated_by",
      "tenant",
      "created_by_id",
      "updated_by_id",
      "tenant_id",
    ];
    return fileColumns.filter(
      column => !systemFields.includes(column.toLowerCase())
    );
  };

  const handleColumnMapping = async (values: any) => {
    setMappingError("");

    // Filter out empty values and create mapping
    const mapping: ColumnMapping[] = Object.entries(values)
      .filter(([, value]) => value && value !== "")
      .map(([key, value]) => ({
        file_column_name: value as string,
        db_column_name: key,
      }));

    if (mapping.length === 0) {
      setMappingError("Please map at least one column before proceeding.");
      notify.warning(
        "No Mappings",
        "Please map at least one column before proceeding."
      );
      return;
    }

    // Check for duplicate file_column_name
    const fileNames = mapping.map(m => m.file_column_name);
    const hasDuplicates = fileNames.length !== new Set(fileNames).size;
    if (hasDuplicates) {
      setMappingError(
        "Each file column can only be mapped to one database field."
      );
      return;
    }

    if (importId) {
      await handleUpdateMapping(importId, mapping);
    } else {
      setMappingError("No import ID available. Please upload a file first.");
    }
  };

  const handleResetMapping = () => {
    form.resetFields();
    setMappingError("");
    onMappingUpdate(false);
    notify.success("Mapping Reset", "Column mapping has been reset.");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Text strong>Column Mapping</Text>
        <CustomButton
          size="small"
          onClick={handleResetMapping}
          icon={<RefreshCw size={14} />}
          disabled={disabled}
          title="Reset Column Mapping"
        >
          Reset
        </CustomButton>
      </div>

      {!columnGridData ? (
        <div className="py-8 text-center">
          <Spin size="large" />
          <Text className="mt-4 block">Loading database schema...</Text>
        </div>
      ) : dbColumns.length === 0 ? (
        <Alert
          type="error"
          message="No database fields available for mapping"
          description="Please check your schema configuration."
          showIcon
        />
      ) : (
        <Form
          form={form}
          onFinish={handleColumnMapping}
          layout="vertical"
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {dbColumns.map((dbColumn: { label: string; value: string }) => (
              <Card key={dbColumn.value}>
                <Form.Item
                  label={
                    <Space>
                      <Text strong>{dbColumn.label}</Text>
                      <Text type="secondary" className="text-xs">
                        ({dbColumn.value})
                      </Text>
                    </Space>
                  }
                  name={dbColumn.value}
                >
                  <Select
                    placeholder="Select file column"
                    allowClear
                    options={getMappableFileColumns().map(fileColumn => {
                      const currentValues = form.getFieldsValue();
                      const isDisabled = Object.entries(currentValues).some(
                        ([key, val]) =>
                          key !== dbColumn.value && val === fileColumn
                      );
                      return {
                        label: fileColumn,
                        value: fileColumn,
                        disabled: isDisabled,
                      };
                    })}
                    showSearch
                    filterOption={filterByLabel}
                  />
                </Form.Item>
              </Card>
            ))}
          </div>

          {mappingError && (
            <Alert
              type="error"
              message={mappingError}
              showIcon
              className="mb-4"
            />
          )}

          {fileColumns.length > getMappableFileColumns().length && (
            <Alert
              type="info"
              message="System fields are automatically handled"
              description="System fields (id, is_active, created_on, etc.) are automatically handled during import and don't need to be mapped."
              showIcon
              className="mb-4"
            />
          )}

          <div className="space-y-4">
            <CustomButton
              type="primary"
              loading={isUpdatingMapping}
              disabled={disabled}
              onClick={() => {
                const values = form.getFieldsValue();
                const hasValues = Object.values(values).some(
                  val => val && val !== ""
                );

                if (!hasValues) {
                  notify.warning(
                    "No Mappings",
                    "Please map at least one column before proceeding."
                  );
                  return;
                }

                handleColumnMapping(values);
              }}
              title="Save Column Mapping"
            >
              {isUpdatingMapping ? "Updating..." : "Update Mapping"}
            </CustomButton>

            {isMappingSaved && (
              <CustomButton
                type="primary"
                loading={isRunningImport}
                disabled={disabled}
                onClick={onImportRun}
                icon={<Play size={14} />}
                size="large"
                className="w-full"
                title="Execute Import Process"
              >
                {isRunningImport ? "Importing..." : "Start Import"}
              </CustomButton>
            )}
          </div>
        </Form>
      )}
    </div>
  );
};

export default ColumnMappingStep;
